import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenuAlt3, HiX } from 'react-icons/hi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import './Navbar.css';

const sectionIds = ['hero', 'about', 'experience', 'projects', 'skills', 'contact'];
const navKeys = ['home', 'about', 'experience', 'projects', 'skills', 'contact'];

const Navbar = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  const isHome = location.pathname === '/';

  const navLinks = sectionIds.map((id, i) => ({
    name: t(`nav.${navKeys[i]}`),
    sectionId: id,
  }));

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      if (!isHome) return;
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(sectionIds[i]);
        if (el && el.getBoundingClientRect().top <= 150) {
          setActiveSection(sectionIds[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  const handleClick = (sectionId) => {
    setMobileOpen(false);
    if (isHome) {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  return (
    <>
      <motion.nav
        className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="navbar__inner container">
          <a
            href="/"
            className="navbar__logo"
            onClick={(e) => {
              e.preventDefault();
              handleClick('hero');
            }}
          >
            <span className="navbar__logo-bracket">&lt;</span>
            GM
            <span className="navbar__logo-bracket"> /&gt;</span>
          </a>

          <div className="navbar__right">
            <div className="navbar__links">
              {navLinks.map((link) => (
                <a
                  key={link.sectionId}
                  href={`/#${link.sectionId}`}
                  className={`navbar__link ${isHome && activeSection === link.sectionId ? 'navbar__link--active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleClick(link.sectionId);
                  }}
                >
                  {link.name}
                  {isHome && activeSection === link.sectionId && (
                    <motion.div className="navbar__link-indicator" layoutId="activeNav" />
                  )}
                </a>
              ))}
            </div>
            <LanguageSwitcher />
          </div>

          <div className="navbar__mobile-right">
            <LanguageSwitcher />
            <button
              className="navbar__toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <HiX /> : <HiMenuAlt3 />}
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="mobile-menu__links">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.sectionId}
                  href={`/#${link.sectionId}`}
                  className="mobile-menu__link"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 + 0.1 }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleClick(link.sectionId);
                  }}
                >
                  <span className="mobile-menu__link-num">0{i + 1}.</span>
                  {link.name}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
